import { useMemo, useRef } from "preact/hooks";

import { AnimalCard } from "../components/AnimalCard";
import { GardenChip } from "../components/GardenChip";
import { HeatAdvisorySection } from "../components/HeatAdvisorySection";
import { InfoTooltip } from "../components/InfoTooltip";
import { WeatherAlertsBanner } from "../components/WeatherAlertsBanner";
import { WeatherMapPanel } from "../components/WeatherMapPanel";
import { useAnimalHealthNotes } from "../hooks/useAnimalHealthNotes";
import { useAnimals } from "../hooks/useAnimals";
import { useFocusHeadingOnMount } from "../hooks/useFocusHeadingOnMount";
import type { useTeamBoard } from "../hooks/useTeamBoard";
import { useProjects } from "../hooks/useProjects";
import { useWeather } from "../hooks/useWeather";
import { attachmentThumbUrl } from "../lib/api";
import { weekdayShortLabel } from "../lib/calendarDates";
import { timeOfDayGreeting } from "../lib/greeting";
import { HELP_COPY } from "../lib/helpCopy";
import { firstName } from "../lib/personColor";
import {
  ANIMALS_EMPTY_HINT,
  PROJECTS_EMPTY_HINT,
  healthEventTypeLabel,
  monthDayLabel,
  projectStatusLabel,
} from "../lib/recordsCopy";
import {
  WEATHER_FULL_FORECAST_LABEL,
  WEATHER_FULL_FORECAST_NEW_TAB_HINT,
  WEATHER_LOADING_TEXT,
  WEATHER_UNAVAILABLE_TEXT,
  currentMetaLabel,
  precipChanceLabel,
  weatherAsOfLabel,
  weatherFullForecastUrl,
  weatherIconGlyph,
} from "../lib/weatherCopy";
import type {
  AnimalOut,
  DailyForecastOut,
  DueResponse,
  DueTask,
  FlexibleProgress,
  TeamMemberBoard,
} from "../types";

export interface DashboardViewProps {
  /** Full display name — `firstName()` derives the greeting's first name,
   * matching every other greeting in this app (`app.tsx`'s retired phone
   * app-bar row, `Sidebar.tsx`'s footer). */
  name: string;
  /** Same `DATE_FORMATTER.format(data.date)` string `app.tsx`'s phone
   * app-bar used to render — computed once there, passed down rather
   * than recomputed here (one call site, see `app.tsx`'s own docstring). */
  dateLabel: string;
  /** Same flag `Sidebar`/`TabBar`/every other role-gated screen already
   * threads through — `true` for BOTH an admin and a manager; per
   * §2.1's own table every farm-wide row is gated on this ONE flag, never
   * `isAdmin` (which this view never even receives — Dashboard has no
   * admin-only content, only member-vs-"canHandOutWork" content). */
  canHandOutWork: boolean;
  /** The signed-in person's own `GET /due?scope=mine` response — already
   * fetched by `app.tsx` for Today/My list; reused here verbatim, no
   * second fetch (§2.1: "reuse the exact same hooks the phone app already
   * has"). */
  data: DueResponse;
  /** `app.tsx`'s own `useTeamBoard(canHandOutWork)` instance — reused
   * verbatim for the farm-wide admin/manager rows (§2.1's `useTeamBoard`
   * column), no second `GET /team/board` / `GET /due?scope=team` call. */
  team: ReturnType<typeof useTeamBoard>;
  /** Card click-throughs (§5, R-A0-2) — the SAME `navigate` `app.tsx`
   * threads to `Sidebar`/`TodayView`; route changes it drives focus the
   * destination view's own `<h1 tabindex="-1">` on mount exactly as every
   * other in-app navigation already does (no bespoke focus handling
   * needed here). */
  onNavigate: (path: string) => void;
}

const DAY_MS = 24 * 60 * 60 * 1000;
/** §2.3 / m-A0-3: "Recent animal events" lookback cap. */
const RECENT_EVENTS_WINDOW_DAYS = 90;
/** §2.1: up to 6 animals shown (and fanned out for health events). */
const ANIMALS_SHOWN = 6;
/** §2.3: top 5 rows once sorted/filtered. */
const MAX_ATTENTION_ROWS = 5;
const MAX_EVENT_ROWS = 5;
const MAX_PROJECT_ROWS = 5;
const NOTE_TRUNCATE_LENGTH = 70;

function truncateNote(note: string | null): string {
  if (!note) return "";
  const trimmed = note.trim();
  return trimmed.length > NOTE_TRUNCATE_LENGTH
    ? `${trimmed.slice(0, NOTE_TRUNCATE_LENGTH).trimEnd()}…`
    : trimmed;
}

function onTodayNamesLabel(members: TeamMemberBoard[]): string {
  const active = members.filter((m) => m.open_count + m.done_today_count > 0);
  if (active.length === 0) return "Nobody's on today yet";
  const names = active.map((m) => firstName(m.name) || m.name);
  if (names.length <= 2) return names.join(", ");
  return `${names.slice(0, 2).join(", ")} +${names.length - 2} more`;
}

/** §2.3's "Needs attention" row shape — the same subset of `DueTask`
 * fields for both the farm-wide (`teamDue.overdue`) and personal
 * (`data.overdue`) source lists. */
interface AttentionRow {
  key: string;
  title: string;
  area: string | null;
  assigneeName: string | null;
  overdueText: string;
}

function toAttentionRows(tasks: DueTask[], showAssignee: boolean): AttentionRow[] {
  return tasks.slice(0, MAX_ATTENTION_ROWS).map((task) => ({
    key: task.id,
    title: task.title,
    area: task.area,
    assigneeName: showAssignee ? task.assignee_name : null,
    overdueText: task.overdue_phrase ?? task.phrase,
  }));
}

interface RecentEventRow {
  key: string;
  animalName: string;
  typeLabel: string;
  dateLabel: string;
  note: string;
  createdAtMs: number;
}

/** Sprint-015 C4 (weather-hub-ia-layout-2026-07-10.md §5, markup nit) —
 * one day-card implementation, rendered TWICE: once inside the unmodified
 * `<1280px` `.weather-forecast` horizontal-scroll strip, once inside the
 * new `≥1280px` `.weather-forecast-grid` (CSS-only breakpoint swap
 * between the two wrappers, matching this app's existing convention for
 * `.dashboard-grid-2`'s own `≥1280px` promotion — no JS hook for this
 * breakpoint). Markup is IDENTICAL between the two renders (the spec's
 * own "one responsive component, not two designs" framing) — shipped
 * `.weather-day-temps` wrapping `.weather-day-hi`/`-lo`, unchanged from
 * the pre-C4 single-render version. */
function WeatherDayCard(day: DailyForecastOut) {
  return (
    <div key={day.date} class="weather-day">
      <p class="weather-day-label">{weekdayShortLabel(new Date(`${day.date}T00:00:00`))}</p>
      <p class="weather-day-icon" aria-hidden="true">
        {weatherIconGlyph(day.condition_code)}
      </p>
      <p class="weather-day-temps">
        <span class="weather-day-hi">{Math.round(day.high_f)}°</span>
        <span class="weather-day-lo">{Math.round(day.low_f)}°</span>
      </p>
      <p class="weather-day-precip">{precipChanceLabel(day.precip_chance_pct)}</p>
    </div>
  );
}

export function DashboardView({
  name,
  dateLabel,
  canHandOutWork,
  data,
  team,
  onNavigate,
}: DashboardViewProps) {
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  // Sprint-008 A4 (R-A0-2) — route-change focus management, the SAME
  // governed convention every other routed view in this sprint reuses
  // (see `TodayView.tsx`'s docstring for the full rationale). Dashboard's
  // own `<h1>` IS the greeting (§1.5/§2.2) — this view is the greeting's
  // new, one true home now that the phone app-bar's greeting row is
  // hidden at desktop (`app.tsx`).
  useFocusHeadingOnMount(headingRef);

  const animals = useAnimals(true);
  const projects = useProjects(canHandOutWork);
  const weather = useWeather();

  // ux-ruling-inactive-grid-2026-07-10.md — E1 PM ruling item 2 (BINDING):
  // "Dashboard mini-grid stays active-only unconditionally — no toggle on
  // that surface." Unlike `AnimalsView`'s Active/All toggle, this filter
  // is not user-switchable here — anyone wanting to find an inactive
  // animal goes to the full Animals view.
  const shownAnimals = useMemo<AnimalOut[]>(
    () => (animals.animals ?? []).filter((a) => a.active).slice(0, ANIMALS_SHOWN),
    [animals.animals],
  );
  const animalIds = useMemo(() => shownAnimals.map((a) => a.id), [shownAnimals]);
  const animalNameById = useMemo(
    () => new Map(shownAnimals.map((a) => [a.id, a.name])),
    [shownAnimals],
  );
  const {
    eventsByAnimal,
    loading: eventsLoading,
    recentNoteLevel,
  } = useAnimalHealthNotes(animalIds);

  // A stable "now" for the whole card render, not a fresh `Date.now()`
  // per row — matches §2.3's own "runs once on Dashboard mount" framing
  // for the fan-out this reference gates.
  const nowMs = useMemo(() => Date.now(), []);
  function daysAgo(iso: string): number {
    return (nowMs - new Date(iso).getTime()) / DAY_MS;
  }

  const isMember = !canHandOutWork;

  // ---- Stat tiles (§2.3) ------------------------------------------------
  const members = team.board?.members ?? null;
  // "On today" has a real, distinct copy state for "zero people with
  // anything on today" ("Nobody's on today yet") — worth not conflating with
  // "still loading," which every OTHER stat tile's own "0/0 reads fine"
  // (§2.3) can safely ignore (a bare 0 reads the same whether it means
  // "loading" or "genuinely zero").
  const onTodayKnown = isMember || members !== null;
  const todayDone = isMember
    ? data.done_today.length
    : (members ?? []).reduce((sum, m) => sum + m.done_today_count, 0);
  // "total" for the personal tile is a flagged interpretation — §2.3's
  // table names only "data.done_today.length / total (useDue)" without
  // defining `total` explicitly. This app's own `TeamMemberBoard.
  // open_count` is a TODAY-scoped open-assignment count (`GET /team/
  // board`, which — like `GET /due?scope=team` — is itself a today-
  // scoped read); the closest personal equivalent already on hand from
  // `useDue()` is every task still open TODAY (overdue + due today +
  // whenever), so `total` here is `done_today.length` plus those three,
  // mirroring `done_today_count + open_count`'s own shape exactly.
  const todayTotal = isMember
    ? data.done_today.length + data.overdue.length + data.due_today.length + data.whenever.length
    : (members ?? []).reduce((sum, m) => sum + m.done_today_count + m.open_count, 0);

  const overdueCount = isMember ? data.overdue.length : (team.teamDue?.overdue.length ?? 0);

  // ---- Needs attention (§2.3) -------------------------------------------
  const attentionRows = isMember
    ? toAttentionRows(data.overdue, false)
    : toAttentionRows(team.teamDue?.overdue ?? [], true);
  const attentionKnown = isMember || team.teamDue !== null;

  // ---- This week / flexible progress (§2.3, §2.5.1) ----------------------
  // `null` = not yet known (still loading, admin/manager only — a member's
  // `flexible_progress` is always already on hand via `data`, no loading
  // state of its own); `[]` = known-empty; populated = known-content.
  const flexibleList: FlexibleProgress[] | null = isMember
    ? data.flexible_progress
    : (team.teamDue?.flexible_progress ?? (team.loading ? null : []));
  const flexibleKnownEmpty = flexibleList !== null && flexibleList.length === 0;

  // ---- Recent animal events (§2.3, m-A0-3) -------------------------------
  const recentEvents = useMemo<RecentEventRow[]>(() => {
    const rows: RecentEventRow[] = [];
    for (const [animalId, events] of Object.entries(eventsByAnimal)) {
      const animalName = animalNameById.get(animalId);
      if (!animalName) continue;
      for (const event of events) {
        if (daysAgo(event.created_at) > RECENT_EVENTS_WINDOW_DAYS) continue;
        rows.push({
          key: event.id,
          animalName,
          typeLabel: healthEventTypeLabel(event.event_type),
          dateLabel: monthDayLabel(event.event_date),
          note: truncateNote(event.note),
          createdAtMs: new Date(event.created_at).getTime(),
        });
      }
    }
    rows.sort((a, b) => b.createdAtMs - a.createdAtMs);
    return rows.slice(0, MAX_EVENT_ROWS);
    // `nowMs`/`daysAgo` are stable for the life of this mount;
    // `eventsByAnimal`/`animalNameById` are this computation's only real
    // inputs.
  }, [eventsByAnimal, animalNameById]);

  // ---- Projects & status (§2.1: admin/manager only; §2.3) ----------------
  const projectRows = canHandOutWork ? (projects.projects ?? []).slice(0, MAX_PROJECT_ROWS) : [];

  function WeatherCard() {
    const data = weather.weather;
    const isAvailable = !weather.loading && !weather.error && data?.available && data.current;
    // Sprint-018 E5 (#55) — Wunderground coordinate-form URL built
    // client-side from the farm's own `latitude`/`longitude` (never a
    // hardcoded city slug); `null` when the farm point isn't configured,
    // same "no half-broken affordance" contract `WeatherMapPanel`'s own
    // lat/lon gate follows.
    const fullForecastUrl = data ? weatherFullForecastUrl(data.latitude, data.longitude) : null;
    return (
      <div class={`dashboard-card dashboard-weather-card${isAvailable ? " is-available" : ""}`}>
        <h2 class="dashboard-card-head">Weather</h2>
        {/* ADR-weather-map-provider-2026-07-10, keyless phase 1 — SA
         * Amendment 1 condition 1 (BINDING): rendered ABOVE the
         * loading/error/unavailable/available branching below, off
         * `data?.alerts` alone, so an active NWS alert still shows even
         * when the forecast itself is `available: false`. */}
        <WeatherAlertsBanner
          alerts={data?.alerts ?? []}
          alertsFetchedAt={data?.alerts_fetched_at ?? null}
          alertsStale={data?.alerts_stale ?? false}
        />
        {weather.loading ? (
          <p class="loading-hint">{WEATHER_LOADING_TEXT}</p>
        ) : weather.error ? (
          <p class="error-hint" role="alert">
            {weather.error}
          </p>
        ) : !isAvailable || !data?.current ? (
          <p>{WEATHER_UNAVAILABLE_TEXT}</p>
        ) : (
          <>
            {/* Sprint-015 C4 (weather-hub-ia-layout-2026-07-10.md §5) —
             * `<1280px`: stacks (this wrapper has no base grid rule, only
             * a `≥1280px` media query — see `styles.css`), left/right
             * columns render one after another, same shape as the
             * pre-C4 single-column card. `≥1280px`: two-column grid,
             * `minmax(380px,1fr) minmax(440px,1.15fr)`, radar promoted
             * into a real, always-visible right column (reconciled with
             * `WeatherMapPanel`'s own always-expanded-at-desktop
             * behavior, CD sign-off Deviation 2). Radar map moved INSIDE
             * this branch (previously a sibling of the whole ternary,
             * gated on a duplicate `isAvailable` check) — same rendering
             * condition as before (`isAvailable` is already true to be
             * in this branch; only `latitude`/`longitude` non-null still
             * needs checking), preserves the existing "unavailable card
             * is never interactive" contract unchanged. */}
            <div class="weather-hub-body">
              <div class="weather-hub-left">
                <div class="weather-current">
                  <span class="weather-icon" aria-hidden="true">
                    {weatherIconGlyph(data.current.condition_code)}
                  </span>
                  <span class="weather-temp">{Math.round(data.current.temp_f)}°</span>
                  <span class="weather-condition">{data.current.condition_label}</span>
                </div>
                <p class="weather-meta">
                  {currentMetaLabel(data.current.wind_mph, data.current.humidity_pct)}
                </p>
                {data.as_of ? (
                  <p class="weather-asof">
                    {weatherAsOfLabel(data.as_of, data.stale)}
                    {data.stale ? (
                      <InfoTooltip
                        label={HELP_COPY.weatherStale.label}
                        content={HELP_COPY.weatherStale.content}
                      />
                    ) : null}
                  </p>
                ) : null}
                {/* Sprint-015 C1/C2 — conditional heat-stress advisory
                 * pill/hero. Placed inside the `isAvailable` branch (not
                 * independent of it, unlike the NWS banner above) —
                 * advisory/garden are computed from the same cached
                 * forecast+THI data this branch already gates on, and
                 * this preserves the existing "unavailable card is never
                 * interactive" contract (`dashboard-view.test.tsx`)
                 * without needing a second gate. `alertsActive`
                 * implements C2's binding NWS-precedence conditional. */}
                <HeatAdvisorySection
                  advisory={data.advisory}
                  alertsActive={(data.alerts?.length ?? 0) > 0}
                />
                {/* Sprint-015 C3 (§4) — garden water chip, below the
                 * heat-status element at every state (livestock
                 * outranks garden convenience). */}
                <GardenChip garden={data.garden} />
              </div>
              <div class="weather-hub-right">
                {data.latitude !== null && data.longitude !== null ? (
                  <WeatherMapPanel
                    lat={data.latitude}
                    lon={data.longitude}
                    mapBaseLayer={data.map_base_layer}
                  />
                ) : null}
              </div>
            </div>
            {/* Sprint-015 C4 — 7-day forecast row, full width, below both
             * columns. `<1280px` keeps the unmodified `.weather-forecast`
             * horizontal-scroll strip; `≥1280px` promotes to the sibling
             * `.weather-forecast-row-wrap`/`.weather-forecast-grid`
             * (`repeat(7,1fr)`, all 7 days, no scroll) — CSS toggles
             * which one is visible at the 1280px breakpoint (same "no JS
             * hook for this breakpoint" convention `.dashboard-grid-2`
             * already established), each rendering the SAME
             * `WeatherDayCard` per day. */}
            <div class="weather-forecast">{data.daily.map((day) => WeatherDayCard(day))}</div>
            <div class="weather-forecast-row-wrap">
              <div class="weather-forecast-row-head">
                <p class="weather-forecast-row-title">7-day forecast</p>
              </div>
              <div class="weather-forecast-grid">
                {data.daily.map((day) => WeatherDayCard(day))}
              </div>
            </div>
            {/* Farm point unconfigured (rare — `WeatherMapPanel` above
             * degrades the same way) -> omit the link entirely rather than
             * ship a broken/generic href; the rest of the available card
             * still renders unchanged. */}
            {fullForecastUrl ? (
              <a
                class="weather-full-forecast-link"
                href={fullForecastUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {WEATHER_FULL_FORECAST_LABEL}
                <span aria-hidden="true">↗</span>
                <span class="weather-full-forecast-hint">{WEATHER_FULL_FORECAST_NEW_TAB_HINT}</span>
              </a>
            ) : null}
          </>
        )}
      </div>
    );
  }

  function AnimalsSnapshotCard() {
    return (
      <div class="dashboard-card">
        <div class="dashboard-card-head-row">
          <h2 class="dashboard-card-head">Animals</h2>
          {/* Sprint-016 F3 (DASH-01, ux-audit-whole-app-2026-07-11.md —
           * S3, CD binding condition 5) — the ONE honest navigational
           * affordance per card, replacing the per-row chevron/button
           * this mini-grid used to carry. Distinct accessible name (never
           * a bare "See all" repeated across cards — WCAG 2.4.4). */}
          <button type="button" class="inline-link-btn" onClick={() => onNavigate("/animals")}>
            See all animals →
          </button>
        </div>
        {animals.loading ? (
          <p class="loading-hint">Loading animals…</p>
        ) : animals.error ? (
          <p class="error-hint" role="alert">
            {animals.error}
          </p>
        ) : shownAnimals.length === 0 ? (
          <p class="empty-hint">{ANIMALS_EMPTY_HINT}</p>
        ) : (
          // Sprint-012 — shares `../components/AnimalCard` (design-spec-
          // animal-cards-2026-07-10.md §2, Option B) with `AnimalsView`'s
          // full-page grid, consolidating what used to be a second
          // copy-pasted card implementation here (UX §1.1). Sprint-016 F3
          // (DASH-01) — `interactive={false}`: every card click-through
          // has always landed on `/animals` (§2.1 unchanged), never this
          // animal's own detail overlay, so the chevron/button affordance
          // was over-promising "open this exact animal." `onOpen` stays
          // wired (the prop is required) but is now unreachable — the
          // card renders as a plain, non-interactive preview row.
          <ul class="dashboard-animal-list">
            {shownAnimals.map((animal) => (
              <li key={animal.id}>
                <AnimalCard
                  animal={animal}
                  hasHealthNote={!eventsLoading && recentNoteLevel(animal.id) === "attention"}
                  hasRoutineCare={!eventsLoading && recentNoteLevel(animal.id) === "routine"}
                  onOpen={() => onNavigate("/animals")}
                  photoUrl={
                    animal.primary_photo_attachment_id
                      ? attachmentThumbUrl(animal.primary_photo_attachment_id)
                      : undefined
                  }
                  interactive={false}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  function ThisWeekCard() {
    return (
      <div class="dashboard-card">
        <h2 class="dashboard-card-head">This week</h2>
        {flexibleList === null ? (
          <p class="loading-hint">Loading…</p>
        ) : (
          <ul class="dashboard-flexible-list">
            {flexibleList.map((item) => {
              const pct =
                item.target_count > 0
                  ? Math.min(100, (item.done_count / item.target_count) * 100)
                  : 0;
              return (
                <li key={item.cadence_id}>
                  <div class="dashboard-flexible-row">
                    <p class="dashboard-flexible-title">{item.title}</p>
                    <div class="parent-progress-track">
                      <div class="parent-progress-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <p class="dashboard-flexible-meta">{item.phrase}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }

  function RecentEventsCard() {
    return (
      <div class="dashboard-card">
        <div class="dashboard-card-head-row">
          <h2 class="dashboard-card-head">Recent animal events</h2>
          <button type="button" class="inline-link-btn" onClick={() => onNavigate("/animals")}>
            See all recent events →
          </button>
        </div>
        {animals.loading || eventsLoading ? (
          <p class="loading-hint">Loading…</p>
        ) : recentEvents.length === 0 ? (
          <p class="empty-hint">No recent notes.</p>
        ) : (
          <ul class="dashboard-event-list">
            {recentEvents.map((row) => (
              <li key={row.key}>
                <div class="dashboard-event-row">
                  {/* Sprint-016 F3 (DASH-01) — downgraded from a real
                   * `<button onClick>` to a plain `<div>`: no click
                   * handler, no button semantics — a static preview row,
                   * same posture as the "See all recent events" link
                   * above it now owns the ONE navigational affordance. */}
                  <div class="dashboard-event-btn">
                    <span>
                      <strong class="dashboard-event-animal">{row.animalName}</strong> —{" "}
                      {row.typeLabel}, {row.dateLabel}
                      {row.note ? `: ${row.note}` : ""}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  function ProjectsCard() {
    return (
      <div class="dashboard-card">
        <div class="dashboard-card-head-row">
          <h2 class="dashboard-card-head">Projects &amp; status</h2>
          <button type="button" class="inline-link-btn" onClick={() => onNavigate("/projects")}>
            See all projects →
          </button>
        </div>
        {projects.loading ? (
          <p class="loading-hint">Loading projects…</p>
        ) : projects.error ? (
          <p class="error-hint" role="alert">
            {projects.error}
          </p>
        ) : projectRows.length === 0 ? (
          <p class="empty-hint">{PROJECTS_EMPTY_HINT}</p>
        ) : (
          <ul class="dashboard-project-list">
            {projectRows.map((project) => {
              const statusLabel = projectStatusLabel(project.status);
              return (
                <li key={project.id}>
                  {/* Sprint-016 F3 (DASH-01) — downgraded from a real
                   * `<button onClick>` to a plain `<div>`; "See all
                   * projects" above now owns the ONE navigational
                   * affordance for this card. */}
                  <div class="dashboard-project-row">
                    <span class="dashboard-project-name">{project.name}</span>
                    {statusLabel ? (
                      <span class="dashboard-project-status">{statusLabel}</span>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  }

  return (
    <section class="view" aria-label="Dashboard">
      <header class="page-header">
        <h1 ref={headingRef} tabIndex={-1} class="view-heading">
          {timeOfDayGreeting()}, {firstName(name) || name}
        </h1>
        <p class="view-subheading">{dateLabel}</p>
      </header>

      {/* Sprint-009 B5 (design-phase-b.md §3.1) — fills the reserved
       * weather slot A4 left dashed/"coming soon." Solid border once
       * `available: true` (§3.1's own "keep the dashed treatment reserved
       * exclusively for the still-unavailable case" rule); real heading +
       * real body text either way, never interactive, never `aria-hidden`
       * (an informational state is still information — §5). */}
      <WeatherCard />

      <div class="dashboard-stat-row">
        <div class="dashboard-stat-tile">
          <p class="dashboard-stat-label">Today's progress</p>
          <p class="dashboard-stat-value">
            {todayDone}/{todayTotal} chores done today
          </p>
        </div>
        <div
          class={`dashboard-stat-tile dashboard-stat-tile--overdue${overdueCount === 0 ? " is-zero" : ""}`}
        >
          <p class="dashboard-stat-label">Overdue</p>
          <p class="dashboard-stat-value">{overdueCount}</p>
        </div>
        {canHandOutWork ? (
          <div class="dashboard-stat-tile">
            <p class="dashboard-stat-label">On today</p>
            <p class="dashboard-stat-names">
              {onTodayKnown ? onTodayNamesLabel(members ?? []) : "Loading…"}
            </p>
          </div>
        ) : null}
      </div>

      <div class="dashboard-card dashboard-card--attn">
        <h2 class="dashboard-card-head">Needs attention</h2>
        {!attentionKnown ? (
          <p class="loading-hint">Loading…</p>
        ) : attentionRows.length === 0 ? (
          <p class="dashboard-attn-empty">Nothing overdue — nice work.</p>
        ) : (
          <ul class="dashboard-attn-list">
            {attentionRows.map((row) => (
              <li key={row.key}>
                <button
                  type="button"
                  class="dashboard-attn-row"
                  onClick={() => onNavigate(canHandOutWork ? "/team" : "/my")}
                >
                  <span class="dashboard-attn-dot" aria-hidden="true" />
                  <span class="dashboard-attn-main">
                    <span class="dashboard-attn-title">
                      {row.title}
                      {row.assigneeName ? ` — ${row.assigneeName}` : ""}
                    </span>
                    <span class="dashboard-attn-meta">{row.overdueText}</span>
                  </span>
                  {row.area ? <span class="dashboard-attn-chip">{row.area}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {flexibleKnownEmpty ? (
        <AnimalsSnapshotCard />
      ) : (
        <div class="dashboard-grid-2">
          <AnimalsSnapshotCard />
          <ThisWeekCard />
        </div>
      )}

      {canHandOutWork ? (
        <div class="dashboard-grid-2">
          <RecentEventsCard />
          <ProjectsCard />
        </div>
      ) : (
        <RecentEventsCard />
      )}
    </section>
  );
}
